import { useState } from "react"
import * as helpers from "../../Helper/utils"
import { addFavorite, removeFavorite } from "../../HTTP/articles"
import { FactChecking } from "./factChecking"
import "./SerperResults.css"

export default function SerperResults({ results, page, loading, error, onPageChange, country }) {
    const [favorites, setFavorites] = useState(() => {
        try {
            const storedFavorites = JSON.parse(localStorage.getItem("favorites") || "[]")
            return Array.isArray(storedFavorites) ? storedFavorites : []
        } catch (error) {
            return []
        }
    })
    const [factCheckLink, setFactCheckLink] = useState(null)
    const hasSubscription = (() => {
        try {
            const subscriptions = JSON.parse(localStorage.getItem("subscriptions"))
            return subscriptions !== null && (subscriptions.data === undefined || subscriptions.data !== null)
        } catch (error) {
            return false
        }
    })()

    const toggleFavorite = async (result) => {
        if (!result.url) return
        const isFavorite = favorites.some((favorite) => favorite.link === result.url)

        try {
            if (isFavorite) {
                const removed = await removeFavorite({ link: result.url })
                if (!removed) throw new Error("Impossible de retirer ce favori.")
                const updatedFavorites = favorites.filter((favorite) => favorite.link !== result.url)
                setFavorites(updatedFavorites)
                localStorage.setItem("favorites", JSON.stringify(updatedFavorites))
            } else {
                const favorite = {
                    title: result.title || result.url,
                    link: result.url,
                    image: result.image || "",
                    country: country.name,
                    publication_date: new Date().toISOString().replace("T", " ").slice(0, 19),
                    description: result.description || "",
                }
                const response = await addFavorite(favorite)
                if (!response?.ok) throw new Error("Impossible d'ajouter ce favori.")
                const updatedFavorites = [{ ...favorite, id: response.id }, ...favorites]
                setFavorites(updatedFavorites)
                localStorage.setItem("favorites", JSON.stringify(updatedFavorites))
            }
        } catch (error) {
            console.error("Erreur lors de la mise à jour du favori :", error)
            alert(error.message || "Une erreur est survenue lors de la mise à jour du favori.")
        }
    }

    return (
        <section className="serper-results" aria-live="polite">
            {loading && <p className="serper-results-status">Recherche en cours...</p>}
            {!loading && error && <p className="serper-results-error" role="alert">{error}</p>}
            {!loading && !error && results.length === 0 && page === 0 && (
                <p className="serper-results-status">Saisissez une recherche pour afficher les résultats.</p>
            )}
            {!loading && !error && results.length === 0 && page > 0 && (
                <p className="serper-results-status">Aucun résultat sur cette page.</p>
            )}

            {results.map((result, index) => {
                const source = result.url ? helpers.getDomainName(result.url) : null
                const sourceCountry = source ? helpers.extractCountryFromUrl(source) : null
                const resultCountry = country.code === "wd" ? sourceCountry : country
                return (
                    <article className="serper-result" key={`${result.url || "result"}-${result.position || index}`}>
                    {result.image && (
                        <img className="serper-result-image" src={result.image} alt="" loading="lazy" />
                    )}
                    <div className="serper-result-content">
                        {result.url ? (
                            <a className="serper-result-title" href={result.url} target="_blank" rel="noopener noreferrer">
                                {result.title || result.url}
                            </a>
                        ) : (
                            <h2 className="serper-result-title">{result.title || "Résultat sans titre"}</h2>
                        )}
                        <p className="serper-result-source">
                            {resultCountry && `${resultCountry.name} | `}
                            {source && `Source : ${source}`}
                        </p>
                        {result.date && <p className="serper-result-date">{result.date}</p>}
                        {result.description && <p className="serper-result-description">{result.description}</p>}
                        {hasSubscription && result.url && (
                            <div className="serper-result-actions">
                                <button
                                    type="button"
                                    className="serper-result-action"
                                    onClick={() => toggleFavorite(result)}
                                    title={favorites.some((favorite) => favorite.link === result.url) ? "Retirer des favoris" : "Ajouter aux favoris"}
                                    aria-label={favorites.some((favorite) => favorite.link === result.url) ? "Retirer des favoris" : "Ajouter aux favoris"}
                                >
                                    <img
                                        src={`${process.env.PUBLIC_URL}/${favorites.some((favorite) => favorite.link === result.url) ? "bookmark.svg" : "bookmarklight.svg"}`}
                                        alt=""
                                    />
                                </button>
                                <button
                                    type="button"
                                    className="serper-result-action serper-fact-check-action"
                                    onClick={() => setFactCheckLink(result.url)}
                                >
                                    Vérificateur des faits
                                </button>
                            </div>
                        )}
                    </div>
                    </article>
                )
            })}

            {page > 0 && (
                <nav className="serper-pagination" aria-label="Pagination des résultats">
                    <button
                        type="button"
                        onClick={() => onPageChange(page - 1)}
                        disabled={loading || page <= 1}
                    >
                        Précédent
                    </button>
                    <button
                        type="button"
                        onClick={() => onPageChange(page + 1)}
                        disabled={loading || results.length === 0}
                    >
                        Suivant
                    </button>
                </nav>
            )}
            {factCheckLink && (
                <FactChecking articleLink={factCheckLink} setShowPopUpFactChecking={(show) => {
                    if (!show) setFactCheckLink(null)
                }} />
            )}
        </section>
    )
}
